/**
 * This service is a bridge between the application and whatever analytics
 * software is used.
 *
 * By defining the REACT_APP_ANALYTICS environment variable we are able to
 * either export the real or mocked implementation, so that from the application
 * point of view nothing changes and it can continue calling the analytics logic
 * as per usuall.
 */
import { cloneDeep, set, merge, get } from 'lodash'
import { qs } from '../routing'

const mock = {
  default: {},
  pageName: '',
  datalayer: {},
  isLive: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  pageBottom: function pageBottom() {},

  setDefaults: function setDefaults(obj: Record<string, unknown>) {
    Object.assign(this.default, obj)
  },
  addToDatalayer(additions: Record<string, unknown>) {
    Object.keys(additions).forEach((key: string) => {
      set(this.datalayer, key, additions[key])
    })
  },
  shouldTrack() {
    const token = qs(document.location.search, 'token')
    const noAuthPage = window.location.pathname.indexOf('authenticate') === -1

    if (!token && !noAuthPage) {
      return false
    }
    return true
  },
  setTrackingData(additions: Record<string, unknown>) {
    const clonedDefaultDatalayer = cloneDeep(this.default)
    const trackingData = {}
    Object.keys(additions).forEach((key) => {
      set(trackingData, key, additions[key])
    })
    window.du_digitalData = merge(
      {},
      clonedDefaultDatalayer,
      this.datalayer,
      trackingData
    )
  },
  trackViewChange: function viewChange() {
    if (this.shouldTrack()) {
      this.track('viewChange')
    }
  },

  trackPage: function page() {
    if (this.shouldTrack()) {
      this.track('page')
    }
  },

  trackInteraction: function interaction() {
    if (this.shouldTrack()) {
      this.track('interaction')
    }
  },

  track: function track(action: string) {
    if (typeof action !== 'string') {
      // eslint-disable-next-line no-console
      console.warn('Legacy tracking code detected')
      return
    }
    // eslint-disable-next-line no-underscore-dangle
    if (!implementation.isLive || window.__CJ_DEBUG_ANALYTICS) {
      // eslint-disable-next-line no-console
      console.log(
        `track ${action}\n pageName: ${get(
          window.du_digitalData,
          this.keys.pageName()
        )}\n viewChange: ${get(
          window.du_digitalData,
          this.keys.viewChange()
        )}\n event0.type: ${get(
          window.du_digitalData,
          this.keys.eventType(0)
        )}\n event0.action: ${get(
          window.du_digitalData,
          this.keys.eventAction(0)
        )}\n event0.linkInformation: ${get(
          window.du_digitalData,
          this.keys.linkInformation(0)
        )}
        \n\n Datalayer:`,
        window.du_digitalData
      )
    }
    // eslint-disable-next-line
    window._satellite && window._satellite.track(action)
  },
  keys: {
    pageName: () => 'core.pageInfo.pageName',
    viewChange: () => 'core.attributes.viewChange',
    eventType: (eventCount: number) =>
      `event[${eventCount}].eventInfo.eventType`,
    eventAction: (eventCount: number) =>
      `event[${eventCount}].eventInfo.eventAction`,
    errorCode: () => 'error.errorCode',
    errorMessage: () => 'error.errorMessage',
    errorCausingURL: () => 'error.errorCausingURL',
    linkInformation: (eventCount: number) =>
      `event[${eventCount}].eventInfo.linkInformation`,
    allowElectronicAds: () => 'dataPrivacyStatement.allowElectronicAds',
  },
}

const real = {
  ...mock,
  isLive: true,
  pageBottom: function pageBottom() {
    window._satellite && window._satellite.pageBottom() // eslint-disable-line
  },
}

let implementation = mock // eslint-disable-line

if (process.env.REACT_APP_ANALYTICS) {
  implementation = real
}

export { implementation as Analytics }
